import { parseNullableDecimal } from 'src/Lca/utils/number';

export type AmountBreakdownPart = {
  unit: string;
  value: number;
};

export type LcaObjectAmountBreakdown = AmountBreakdownPart[] | null;
export type LcaObjectEnergyConversion = {
  source_amount: number;
  energy_per_source_unit: number;
} | null;

export type AmountBreakdownDraftPart = {
  unit: string;
  value: string;
};

export type EnergyConversionDraft = {
  source_amount: string;
  energy_per_source_unit: string;
};

const TRANSPORT_COMPOSITE_UNITS = new Set(['metric ton*km', 'person*km']);
const ENERGY_CONVERSION_UNITS = new Set(['MJ', 'kWh']);

export const parseCompositeUnit = (
  unit: string | null | undefined
): string[] => {
  if (!unit || !unit.includes('*')) {
    return [];
  }

  return unit
    .split('*')
    .map((part) => part.trim())
    .filter(Boolean);
};

export const createAmountBreakdownDraft = (
  unit: string | null | undefined,
  amountBreakdown?: LcaObjectAmountBreakdown
): AmountBreakdownDraftPart[] => {
  const unitParts = parseCompositeUnit(unit);
  if (!unitParts.length) {
    return [];
  }

  const hasMatchingBreakdown =
    amountBreakdown &&
    amountBreakdown.length === unitParts.length &&
    amountBreakdown.every((part, index) => part.unit === unitParts[index]);

  return unitParts.map((unitPart, index) => ({
    unit: unitPart,
    value: hasMatchingBreakdown
      ? String(amountBreakdown?.[index]?.value ?? '')
      : '',
  }));
};

export const toAmountBreakdownPayload = (
  parts: AmountBreakdownDraftPart[]
): LcaObjectAmountBreakdown => {
  if (!parts.length) {
    return null;
  }

  const payload = parts.map((part) => {
    const value = parseNullableDecimal(part.value);
    return value === null ? null : { unit: part.unit, value };
  });

  if (payload.some((part) => part === null)) {
    return null;
  }

  return payload as AmountBreakdownPart[];
};

export const getAmountBreakdownTotal = (
  parts: AmountBreakdownDraftPart[]
): number | null => {
  const payload = toAmountBreakdownPayload(parts);
  if (!payload) {
    return null;
  }

  return payload.reduce((total, part) => total * part.value, 1);
};

export const getTransportDistanceBreakdownIndex = (
  unit: string | null | undefined,
  parts: AmountBreakdownDraftPart[]
): number => {
  if (!unit || !TRANSPORT_COMPOSITE_UNITS.has(unit)) {
    return -1;
  }

  return parts.findIndex((part) => part.unit === 'km');
};

export const isEnergyConversionUnit = (
  unit: string | null | undefined
): boolean => {
  if (!unit) {
    return false;
  }

  return ENERGY_CONVERSION_UNITS.has(unit);
};

export const createEnergyConversionDraft = (
  energyConversion?: LcaObjectEnergyConversion
): EnergyConversionDraft => ({
  source_amount:
    energyConversion?.source_amount !== undefined
      ? String(energyConversion.source_amount)
      : '',
  energy_per_source_unit:
    energyConversion?.energy_per_source_unit !== undefined
      ? String(energyConversion.energy_per_source_unit)
      : '',
});

export const toEnergyConversionPayload = (
  draft: EnergyConversionDraft
): LcaObjectEnergyConversion => {
  const sourceAmount = parseNullableDecimal(draft.source_amount);
  const energyPerSourceUnit = parseNullableDecimal(
    draft.energy_per_source_unit
  );

  if (sourceAmount === null || energyPerSourceUnit === null) {
    return null;
  }

  return {
    source_amount: sourceAmount,
    energy_per_source_unit: energyPerSourceUnit,
  };
};

export const getEnergyConversionTotal = (
  draft: EnergyConversionDraft
): number | null => {
  const payload = toEnergyConversionPayload(draft);
  if (!payload) {
    return null;
  }

  return payload.source_amount * payload.energy_per_source_unit;
};
